import { api } from './client'

export interface Product {
  id: number
  name: string
  slug: string
  code: string | null
  basePrice: number
  /** TASK-0116: omitted entirely by the backend (not null/0) for a caller without
   * CanSeeRealAmounts (TASK-0115 field-level redaction on `ProductsController.List`). */
  commissionValue?: number
  /** REQ-018-002: the operator price list's "Ricavo azienda" — what the company earns from the
   * operator for this product. Redacted the same way as `commissionValue` above (internal margin
   * data, not a customer-facing price): an ABSENT key, never null/0, for a caller without
   * CanSeeRealAmounts. `null` is also a legitimate value for a visible product that simply has no
   * company revenue recorded yet — the two cases are distinguished by `undefined` vs `null`. */
  companyRevenue?: number | null
  pricingType: string | null
  isActive: boolean
  /** Coraly A: solo per lo staff (chiave assente per gli agenti, mai null per redazione). */
  mandanteId?: number | null
  /** Coraly A: solo per lo staff; per gli agenti i prodotti nascosti non arrivano proprio. */
  visibleToAgents?: boolean
  /** Opzione: prodotto che si aggiunge a una pratica, mai prodotto principale. */
  isOption?: boolean
  /** F-055 (TASK-0309): projected by `ProductsController.List` - the practice form filters by
   * department and shows the operator derived from the product. */
  departmentId?: number | null
  operatorId?: number | null
  /** TASK-0156 (REQ-006-007): `"person"` | `"company"` | `null` — `null` means "applies to both",
   * NOT "unset". Unconditionally projected by `ProductsController.List` (not sensitive commission
   * data, unlike `commissionValue` above) — always present in practice, never omitted the way a
   * redacted `commissionValue` is; typed optional here only for consistency with the outbound
   * request shapes below, which genuinely omit it when unset. */
  applicableCustomerType?: string | null
  /** TASK-0164 (REQ-043-002): validity period — `YYYY-MM-DD` calendar date, same format as
   * `Campaign.startDate`/`endDate`. Not sensitive data, same treatment as
   * `applicableCustomerType` above: unconditionally projected by `ProductsController.List`. */
  validFrom?: string | null
  validTo?: string | null
}

export interface Department {
  id: number
  name: string
  wizardType: string | null
  isActive: boolean
}

/**
 * TASK-0077: top-level product category (`ProductCategory.ParentId === null`) — the "Categoria"
 * selector inside the product create/edit form uses this, NOT the list-level "Categoria" filter
 * dropdown in the filter card (that one stays a documented, out-of-scope gap — see
 * `ProductsPage.tsx`). Live data has only 6 rows total (2 top-level: "Energia"/"Telefonia" under
 * their respective departments, 4 second-level subcategories) — `subcategoryId` is deliberately
 * not surfaced anywhere in this file: 0 of 307 real products use it, and wiring a second
 * cascading selector for a field with zero real-world usage isn't a reasonable use of this
 * task's scope. See `ProductsController`'s sibling `ProductCategoriesController` (backend) for
 * the full shape; only the fields this file actually needs are declared here.
 */
export interface ProductCategory {
  id: number
  name: string
  departmentId: number | null
  parentId: number | null
}

/**
 * TASK-0077: commercial operator (e.g. a specific telecom/energy provider) a product can be
 * associated with — `OperatorId` is set on 302 of 307 real products today, so this is included
 * in the product form as a department-filtered selector, reusing the same `departmentId`
 * query-param pattern as `fetchProductCategories`/`fetchDepartments`. See `OperatorsController`
 * (backend) for the full shape; only the fields this file needs are declared here.
 */
export interface Operator {
  id: number
  name: string
  departmentId: number | null
  /** Coraly A: presente solo per lo staff (assente per gli agenti). */
  mandanteId?: number | null
}

/** Coraly A: aggiorna la mandante di un operatore (null = non cambiare, clearMandante = svuota). */
export function updateOperatorMandante(
  id: number,
  mandanteId: number | null,
  changeReason: string,
): Promise<void> {
  const body = mandanteId === null
    ? { clearMandante: true, changeReason }
    : { mandanteId, changeReason }
  return api(`/api/operators/${id}`, { method: 'PUT', body: JSON.stringify(body) })
}

export interface ProductsPage {
  total: number
  page: number
  size: number
  items: Product[]
}

/**
 * TASK-0077: shape of the `product` field in `GET /api/products/{id}`'s response
 * (`ProductsController.Get`, which `Ok()`s `{ product, addons, commissions }` with the full
 * domain entity), used to prefill the edit form. `ProductsController.List`'s projection (what
 * `fetchProducts`/`fetchAllProducts` return as `Product` below) omits `Description`/
 * `PricingType`/`SubcategoryId`/`SddAmount`/`RecurringMonthlyAmount`/`RequiresApproval`
 * entirely — building an edit form from a list row would silently show those fields as blank
 * even for products that already have real values set, and (for the fields the form actually
 * submits) risk sending stale/empty values back. `Product.Update` on the backend guards every
 * field individually (`if (x is not null)`/`if (x.HasValue)`), so an omitted field is never
 * wiped either way — but the *client* still needs the real current value on hand to show it
 * correctly and to let an edit that only touches one field leave the others visibly unchanged.
 */
export interface ProductDetail {
  id: number
  name: string
  slug: string
  code: string | null
  departmentId: number | null
  categoryId: number | null
  subcategoryId: number | null
  operatorId: number | null
  description: string | null
  basePrice: number
  /** TASK-0116: omitted entirely by the backend (not null/0) for a caller without
   * CanSeeRealAmounts (TASK-0115 field-level redaction on `ProductsController.Get`). */
  commissionValue?: number
  /** REQ-018-002: see `Product.companyRevenue` above — same absent-key redaction on
   * `ProductsController.Get` (it is deliberately left off that action's redacted allow-list). */
  companyRevenue?: number | null
  pricingType: string | null
  sddAmount: number | null
  recurringMonthlyAmount: number | null
  requiresApproval: boolean
  isActive: boolean
  createdAt: string
  updatedAt?: string | null
  /** TASK-0156 (REQ-006-007): see `Product.applicableCustomerType`'s doc comment above — same
   * `"person"` | `"company"` | `null` (= "applies to both") vocabulary. */
  applicableCustomerType?: string | null
  /** TASK-0164 (REQ-043-002): see `Product.validFrom`/`validTo` above — same vocabulary, same
   * unconditional (never redacted) exposure on `ProductsController.Get`'s redacted branch too. */
  validFrom?: string | null
  validTo?: string | null
  /** Coraly A: presenti solo per lo staff. */
  mandanteId?: number | null
  visibleToAgents?: boolean
  isOption?: boolean
}

/**
 * TASK-0077: matches `ProductsController.ProductCreateRequest` (backend) field-for-field — all
 * 13 fields the backend accepts, `Name`/`Slug`/`BasePrice`/`CommissionValue` required (matching
 * the backend record's non-nullable positions), the rest nullable/optional. The product form
 * itself only collects a subset of these (see `ProductsPage.tsx`'s `ProductFormState` for which
 * ones and why) — the type stays complete so `createProduct`/`updateProduct` accurately reflect
 * what the backend actually accepts, not just what the current form happens to send.
 *
 * TASK-0116: `commissionValue` here is deliberately left **required** (`number`, not
 * `number | undefined`), unlike `Product.commissionValue`/`ProductDetail.commissionValue` above
 * — this type is an outbound write payload for `Create`/`Update`
 * (`ProductsController.Create`/`Update`, both `[Authorize(PolicyNames.EqualStaff)]` +
 * `CanManageProductsAsync()`-gated, returning 403 otherwise), not an inbound read response, so
 * TASK-0115's redaction (a read-path concern on `List`/`Get`) doesn't apply to it directly and
 * the backend's own `ProductCreateRequest.CommissionValue` (`decimal`, non-nullable) still
 * expects a real number every time. Making this field optional would not fix anything — TS
 * can't tell a redacted-then-`Number(undefined)`-coerced `NaN` apart from a real number at the
 * type level either way (`NaN` still has type `number`), so the actual fix for the one place a
 * missing value could reach this payload (`ProductsPage.tsx`'s edit-prefill from a possibly
 * redacted `ProductDetail`) is a runtime guard in the form, not a looser type here — see
 * `openEdit()`/`handleSave()` in `ProductsPage.tsx`.
 */
export interface ProductCreateRequest {
  /** Coraly A: mandante del prodotto (se null vale quella dell'operatore). */
  mandanteId?: number | null
  /** Coraly A: in Update, true svuota la mandante del prodotto. */
  clearMandante?: boolean
  /** Coraly A: false nasconde il prodotto agli agenti. */
  visibleToAgents?: boolean | null
  /** Opzione (si aggiunge a una pratica). */
  isOption?: boolean | null
  name: string
  slug: string
  code?: string | null
  basePrice: number
  commissionValue: number
  departmentId?: number | null
  categoryId?: number | null
  subcategoryId?: number | null
  operatorId?: number | null
  description?: string | null
  pricingType?: string | null
  sddAmount?: number | null
  recurringMonthlyAmount?: number | null
  requiresApproval?: boolean | null
  /** TASK-0156 (REQ-006-007): matches `ProductsController.ProductCreateRequest.ApplicableCustomerType`
   * (backend) — see `Product.applicableCustomerType`'s doc comment above for the vocabulary. */
  applicableCustomerType?: string | null
  /** TASK-0164 (REQ-043-002): matches `ProductsController.ProductCreateRequest.ValidFrom`/`ValidTo`
   * (backend) — `YYYY-MM-DD`, same format as `Campaign.startDate`/`endDate`. */
  validFrom?: string | null
  validTo?: string | null
  /** REQ-018-002: matches `ProductsController.ProductCreateRequest.CompanyRevenue` (backend) — the
   * operator price list's "Ricavo azienda". Optional here (unlike `commissionValue`, which the
   * backend requires) because the column itself is nullable: omitting it, or sending `null`, leaves
   * an existing value untouched on `Update` (`Product.Update` reads null as "no change"), which is
   * what makes it safe for a caller whose prefill was redacted to submit the form at all. */
  companyRevenue?: number | null
}

/**
 * TASK-0077: matches `ProductsController.ProductUpdateRequest` (backend) — every field
 * optional/nullable, mirroring the backend record exactly. Unlike TASK-0072/0075/0076's
 * targets, `Product.Update` guards every field individually (`if (x is not null)`/
 * `if (x.HasValue)`), so there's no asymmetric-guard data-loss trap here: omitting a field from
 * this payload leaves it untouched server-side, it is never overwritten with null/default.
 */
export type ProductUpdateRequest = Partial<ProductCreateRequest>

export function fetchProducts(params?: {
  page?: number
  pageSize?: number
  search?: string
  departmentId?: number
  /** TASK-0156 (REQ-006-007): `ProductsController.List`'s new `operatorId` filter — the column
   * already existed and was already a form field, but was never filterable in the list before now. */
  operatorId?: number
  /** TASK-0156: `ProductsController.List`'s new `customerType` filter — `"person"` or `"company"`.
   * A product with `applicableCustomerType === null` ("applies to both") is included either way;
   * this narrows to products explicitly restricted to the requested type, plus the unrestricted
   * ones — never excludes the unrestricted ones. */
  customerType?: string
  /** F-055 (TASK-0309): `YYYY-MM-DD` - only products on sale that day (active, within validity). */
  availableOn?: string
  /** Coraly A (solo staff): false = solo i prodotti nascosti agli agenti. */
  visibleToAgents?: boolean
  /** true = solo opzioni, false = solo prodotti principali. */
  isOption?: boolean
}): Promise<ProductsPage> {
  const qs = new URLSearchParams()
  if (params?.page !== undefined) qs.set('page', String(params.page))
  if (params?.pageSize !== undefined) qs.set('size', String(params.pageSize))
  if (params?.search) qs.set('search', params.search)
  if (params?.departmentId !== undefined) qs.set('departmentId', String(params.departmentId))
  if (params?.operatorId !== undefined) qs.set('operatorId', String(params.operatorId))
  if (params?.customerType) qs.set('customerType', params.customerType)
  if (params?.availableOn) qs.set('availableOn', params.availableOn)
  if (params?.visibleToAgents !== undefined) qs.set('visibleToAgents', String(params.visibleToAgents))
  if (params?.isOption !== undefined) qs.set('isOption', String(params.isOption))
  const query = qs.toString()
  return api<ProductsPage>(`/api/products${query ? `?${query}` : ''}`)
}

export function fetchDepartments(): Promise<Department[]> {
  return api<Department[]>('/api/departments')
}

/** TASK-0077: department-filtered top-level product categories for the product form's
 * "Categoria" selector — see `ProductCategory`'s doc comment above. `size: 200` is a generous
 * ceiling (live data has 6 rows total) so pagination never silently truncates this list, same
 * defensive-sizing pattern as `usersApi.list({ size: 200 })` in `api/team.ts`. */
export function fetchProductCategories(params?: { departmentId?: number }): Promise<{ items: ProductCategory[]; total: number }> {
  const qs = new URLSearchParams()
  if (params?.departmentId !== undefined) qs.set('departmentId', String(params.departmentId))
  qs.set('size', '200')
  return api(`/api/productcategories?${qs}`)
}

/** TASK-0077: department-filtered operators for the product form's "Operatore" selector — see
 * `Operator`'s doc comment above. `size: 200` ceiling for the same reason as
 * `fetchProductCategories` (live data has 16 rows total). */
export function fetchOperators(params?: { departmentId?: number }): Promise<{ items: Operator[]; total: number }> {
  const qs = new URLSearchParams()
  if (params?.departmentId !== undefined) qs.set('departmentId', String(params.departmentId))
  qs.set('size', '200')
  return api(`/api/operators?${qs}`)
}

/** TASK-0077: full single-record fetch, used to prefill the edit form — see `ProductDetail`'s
 * doc comment for why this can't be built from a list row. */
export function getProduct(id: number): Promise<{ product: ProductDetail }> {
  return api(`/api/products/${id}`)
}

export function createProduct(data: ProductCreateRequest): Promise<{ id: number }> {
  return api('/api/products', { method: 'POST', body: JSON.stringify(data) })
}

export function updateProduct(id: number, data: ProductUpdateRequest): Promise<void> {
  return api(`/api/products/${id}`, { method: 'PUT', body: JSON.stringify(data) })
}

/** TASK-0077: `ProductsController.Delete` is `[Authorize(PolicyNames.Admin)]`-gated (stricter
 * than Create/Update's `EqualStaff`) and calls `Product.Deactivate()` — a soft delete
 * (`IsActive = false`), not a row removal. `ProductsPage.tsx` accounts for this: since
 * `fetchProducts`/`fetchAllProducts` don't filter by `isActive` (the table already shows an
 * explicit Attivo/Inattivo badge by design), the deleted row is removed from the page's local
 * state directly on a successful call rather than relying on a refetch, which would still
 * include it (correctly) as Inattivo. */
export function deleteProduct(id: number): Promise<void> {
  return api(`/api/products/${id}`, { method: 'DELETE' })
}

// -----------------------------------------------------------------------
// TASK-0073: `ProductsPage` used to call `fetchProducts()` exactly once with a fixed
// `pageSize: 200` and render `res.items` directly, while showing the server's own `total` in the
// header. Live data has 307 products (230 in the "Telefonia" department alone), so the table
// silently dropped everything past the 200th row (alphabetically) — the header's own count
// contradicted the table beneath it, and selecting a department only made it worse (Telefonia's
// 230 also got truncated to 200 by the same fixed page size).
//
// Same shape of bug, same fix, as TASK-0067's `fetchAllPages` in `pipelines.ts` for the Kanban
// board's fixed `pageSize=500`: keep paging until the server's own `total` is satisfied, bounded
// by a generous safety cap so a pathological result set can't turn this into an unbounded loop.

const PRODUCTS_PAGE_SIZE = 200

/** Hard ceiling on products pulled for one listing, as a runaway guard only. Deliberately far
 * above any realistic catalog size (307 products today) so it never truncates in practice. */
const PRODUCTS_MAX_ITEMS = 10000

/**
 * Fetches every product matching `params` (composing with any filter `fetchProducts` already
 * supports, e.g. `departmentId` — TASK-0061's server-side department filter is untouched, this
 * just makes sure a department's full result set is retrieved instead of truncated at one page)
 * by paging `fetchProducts()` until the server-reported `total` is satisfied.
 *
 * TASK-0156: `operatorId`/`customerType` pass straight through to `fetchProducts`, same as
 * `departmentId` — see that function's own doc comments for what each does.
 */
export async function fetchAllProducts(
  params?: { search?: string; departmentId?: number; operatorId?: number; customerType?: string; visibleToAgents?: boolean; isOption?: boolean },
): Promise<{ items: Product[]; total: number }> {
  const all: Product[] = []
  let total = 0
  for (let page = 1; ; page++) {
    const res = await fetchProducts({ ...params, page, pageSize: PRODUCTS_PAGE_SIZE })
    all.push(...res.items)
    total = res.total
    if (
      res.items.length === 0 ||
      res.items.length < PRODUCTS_PAGE_SIZE ||
      all.length >= res.total ||
      all.length >= PRODUCTS_MAX_ITEMS
    ) {
      return { items: all, total }
    }
  }
}
